import { createSelector } from '@reduxjs/toolkit';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { Report } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { LoadLevel } from '@/shared/api/protocol-ts/model/dto_system_load_pb';

import { organizationModel } from '@/entities/organization';
import { studyModel } from '@/entities/study';

import { RootState } from '@/app/model/store';

export const selectUploadKafkaLevel = (state: RootState) =>
  state.highLoad.uploadKafkaLevel;

export const selectUploadCBCTLevel = (state: RootState) =>
  state.highLoad.uploadCBCTLevel;

export const selectReportPipelineLevel = (reportID: string) =>
  createSelector(
    (state: RootState) => state.highLoad.reportLevels[reportID],
    (levels): LoadLevel =>
      levels
        ? Math.max(levels.orgPendingLevel, levels.orgsCountLevel)
        : LoadLevel.LoadLevel_InvalidValue,
  );

// NOTE: the backend routes uploads exclusively - a CBCT session with
// DICOM->DCCT conversion goes through the redis conversion queue and never
// enters the kafka file processing pipeline, everything else is kafka-only.
// The conversion flag is set at session start as (CBCT study && org
// DICOM->DCCT feature flag); the per-session flag is not exposed to the
// client, so we mirror that formula here.
export const selectReportHighLoadLevel = (report: Report | undefined) =>
  createSelector(
    selectReportPipelineLevel(report?.ID ?? ''),
    selectUploadKafkaLevel,
    selectUploadCBCTLevel,
    studyModel.selectors.selectEntities,
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_DICOMToDCCTConversion,
    ),
    (
      pipelineLevel,
      uploadKafkaLevel,
      uploadCBCTLevel,
      studyEntities,
      isDCCTConversionEnabled,
    ): LoadLevel => {
      if (!report) {
        return LoadLevel.LoadLevel_InvalidValue;
      }

      if (report.Status?.Pending) {
        return pipelineLevel;
      }

      if (report.Status?.WaitingForUploadsToComplete) {
        const hasCBCTSource = report.SourceStudyIDs.some(
          (studyID) =>
            studyEntities[studyID]?.Type === StudyType.StudyType_CBCT,
        );

        return hasCBCTSource && isDCCTConversionEnabled
          ? uploadCBCTLevel
          : uploadKafkaLevel;
      }

      return LoadLevel.LoadLevel_InvalidValue;
    },
  );
